"use client";
import { getAllUserFollowing, getUsersForTagging } from "@/api/posts";
import ContentViewModal from "@/components/content-comps/content-view-modal";
import { openContentDetail } from "@/utils/content-navigation";
import {
  removeQueryParamWithoutReload,
  setQueryParamWithoutReload,
} from "@/utils/client-navigation";
import { Modal, OriginModal } from "@/components/origin-modal";
import PlaceDetailsModalForMobile from "@/components/places-comps/mobile/place-details-modal-for-mobile";
import { PlaceAddModal } from "@/components/places-comps/places-add-modal";
import MobileSecondaryScreen from "@/components/places-new-mobile/pl-mobile-screens/places-mobile-secondary-screen";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { getDistance } from "geolib";
import { useTranslations } from "@/lib/static-intl";
import { useRouter, useSearchParams } from "next/navigation";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";

const getInitialLandingCenter = (initialPlacesView) => {
  const latitude = Number(initialPlacesView?.mapCenter?.lat);
  const longitude = Number(initialPlacesView?.mapCenter?.lng);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { lat: latitude, lng: longitude };
};

const PlacesMobileScreen = ({ initialPlacesView = null }) => {
  const apiT = useTranslations("api");
  const { user } = useAuthContext();
  const router = useRouter();
  const searchParams = useSearchParams();
  const viewPlaceId = searchParams.get("view-place");
  const screen = searchParams.get("show");
  const rawSearchPlace = searchParams.get("search-place");
  const initialLandingCenter = getInitialLandingCenter(initialPlacesView);
  const initialLandingSearchQuery = initialPlacesView?.searchQuery || "";
  const hasInitialLandingView = Boolean(
    initialLandingCenter || initialLandingSearchQuery
  );
  const searchPlace = hasInitialLandingView ? null : rawSearchPlace;

  const [searchQuery, setSearchQuery] = useState(
    searchPlace || initialLandingSearchQuery || ""
  );

  const [mapCenter, setMapCenter] = useState(initialLandingCenter);
  // Initialise zoom from the URL (?mz=) so returning from a place detail rebuilds
  // the map at the zoom the user left. Reading it lazily here (before first
  // render) beats the effect-based restore, which lost a race: the map mounted
  // at the default zoom, settled, and overwrote ?mz back to 15 before the effect
  // could apply the URL value. mapCenter already restores via its own effect.
  const [mapZoom, setMapZoom] = useState(() => {
    const urlMz = Number(searchParams?.get("mz"));
    if (urlMz && !Number.isNaN(urlMz) && urlMz > 0) return urlMz;
    return initialPlacesView?.mapZoom || null;
  });
  const [currentUserLocation, setCurrentUserLocation] = useState(
    initialLandingCenter
      ? {
          latitude: initialLandingCenter.lat,
          longitude: initialLandingCenter.lng,
        }
      : null
  );

  const [currentComp, setCurrentComp] = useState(
    screen || initialPlacesView?.show || "map"
  );
  const [isAddPlaceModalOpen, setIsAddPlaceModalOpen] = useState(false);
  const [activeViewPlaceId, setActiveViewPlaceId] = useState(viewPlaceId);

  const playerRef = useRef(null);
  const popupRef = useRef(null);
  const skipInitialScreenParamSyncRef = useRef(
    Boolean(initialPlacesView?.show && !screen)
  );
  const [isPiP, setIsPiP] = useState(false);
  const [popupStyle, setPopupStyle] = useState({});
  const [originalStyle, setOriginalStyle] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedPostInModal, setSelectedPostInModal] = useState(null);
  const [allUsersForTagging, setAllUsersForTagging] = useState([]);
  const [userFollowing, setUserFollowing] = useState([]);
  const {
    isOpen,
    isClosing,
    position,
    handleOriginModalTriggerClick,
    handleOriginModalClose,
    setIsOpen,
  } = OriginModal();

  const openPlaceAddModal = () => setIsAddPlaceModalOpen(true);
  const closePlaceAddModal = () => setIsAddPlaceModalOpen(false);

  const handlePostCardClick = (e, postData) => {
    openContentDetail({
      router,
      content: postData,
      contextType: "place",
    });
  };

  const closeContentModalPopup = () => {
    setIsAnimating(false);
    setPopupStyle(originalStyle);
    setTimeout(() => {
      setSelectedPostInModal(null);
    }, 300);
    handleOriginModalClose();
  };

  const handleEnablePIP = () => {
    setIsPiP(true);
  };

  const handleDisablePIP = () => {
    setIsPiP(false);
  };

  const removeQueryParam = (key) => {
    if (key === "view-place") setActiveViewPlaceId(null);
    removeQueryParamWithoutReload(key, searchParams);
  };

  const addQueryParam = (key, value) => {
    if (key === "view-place") setActiveViewPlaceId(value);
    setQueryParamWithoutReload(key, value, searchParams);
  };

  const switchScreen = (screen) => {
    if (screen) setCurrentComp(screen);
  };

  const applyLocation = ({ latitude, longitude }) => {
    const location = { latitude, longitude };
    console.log("New location received:", location);

    const stringifiedUserLocation = sessionStorage.getItem(
      "hotple-user-location"
    );
    const parsedUserLocation = JSON.parse(stringifiedUserLocation) ?? {};

    if (parsedUserLocation?.latitude && parsedUserLocation?.longitude) {
      console.log("Session location found:", parsedUserLocation);

      const distanceInMeters = getDistance(parsedUserLocation, location);
      console.log(
        "Distance between session and new location:",
        distanceInMeters,
        "meters"
      );

      if (distanceInMeters <= 30) {
        console.log("Distance < 30m → keeping previous location from session.");
        setCurrentUserLocation(parsedUserLocation);
        if (!searchParams.get("mc") && !hasInitialLandingView) {
          console.log("Adding 'mc' (map center) query param");
          addQueryParam(
            "mc",
            `${parsedUserLocation?.latitude},${parsedUserLocation?.longitude}`
          );
          setMapCenter({
            lat: parsedUserLocation?.latitude,
            lng: parsedUserLocation?.longitude,
          });
        }

        if (!searchParams.get("mz") && !hasInitialLandingView) {
          console.log("Adding 'mz' (map zoom) query param");
          addQueryParam("mz", "15");
          setMapZoom(15);
        }
        return;
      }

      console.log("Distance > 30m → updating with new location.");
      sessionStorage.setItem("hotple-user-location", JSON.stringify(location));
    } else {
      console.log("No session location found → saving new location.");
      sessionStorage.setItem("hotple-user-location", JSON.stringify(location));
    }

    setCurrentUserLocation({ latitude, longitude });
    if (!searchParams.get("mc") && !hasInitialLandingView) {
      console.log("No 'mc' (map center) query param found");
      console.log("Adding 'mc' (map center) query param");
      addQueryParam("mc", `${latitude},${longitude}`);
      setMapCenter({ lat: latitude, lng: longitude });
    }

    if (!searchParams.get("mz") && !hasInitialLandingView) {
      console.log("Adding 'mz' (map zoom) query param");
      addQueryParam("mz", "15");
      setMapZoom(15);
    }
  };

  const fetchCurrentUserLocation = async () => {
    const fallbackLocation = {
      latitude: 37.54666563187861,
      longitude: 126.98225129089356, // Seoul
    };

    console.log("Initiating user location fetch...");

    // Step 1: Try Browser's Geolocation API first
    if (navigator.geolocation) {
      console.log("Trying browser Geolocation API (with high accuracy)...");

      try {
        const browserLocation = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (position) => resolve(position),
            (error) => reject(new Error(error?.message)),
            {
              enableHighAccuracy: false,
              timeout: 4000,
              maximumAge: 60000,
            }
          );
        });

        const { latitude, longitude, accuracy } = browserLocation.coords;

        console.log("Browser geolocation success:");
        console.log("   → Latitude:", latitude);
        console.log("   → Longitude:", longitude);
        console.log("   → Accuracy (meters):", accuracy);

        applyLocation({ latitude, longitude });
        return;
      } catch (error) {
        console.warn("Browser Geolocation failed:", error.message);
      }
    } else {
      console.warn("Browser does not support Geolocation.");
    }

    // Step 2: Use fallback location
    console.log("Using fallback location:", fallbackLocation);
    applyLocation(fallbackLocation);
  };

  const fetchUserFollowing = async () => {
    if (user?.userId) {
      try {
        const response = await getAllUserFollowing(user?.userId);
        if (Array.isArray(response)) setUserFollowing(response);
      } catch (error) {
        enqueueSnackbar(
          error?.response?.data?.message ||
            error?.message ||
            apiT("error message"),
          { variant: "error" }
        );
      }
    }
  };

  const fetchAllUsersForTagging = async () => {
    try {
      const response = await getUsersForTagging();
      if (Array.isArray(response)) setAllUsersForTagging(response);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const renderScreen = (screen) => {
    switch (screen) {
      // 초기 화면은 없앴다(2026-07-24 대표님 결정 · 2026-08-13 마저 제거).
      // 모르는 값이 와도 지도로 보낸다 — 들어올 수 있는 화면이 하나뿐이어야
      // 낡은 화면이 다시 되살아나지 않는다.
      case "map":
      default:
        return (
          <MobileSecondaryScreen
            screen="map"
            switchScreen={switchScreen}
            addQueryParam={addQueryParam}
            removeQueryParam={removeQueryParam}
            currentUserLocation={currentUserLocation}
            setCurrentUserLocation={setCurrentUserLocation}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            mapCenter={mapCenter}
            setMapCenter={setMapCenter}
            mapZoom={mapZoom}
            setMapZoom={setMapZoom}
            openPlaceAddModal={openPlaceAddModal}
            initialPlacesView={initialPlacesView}
          />
        );
    }
  };

  useEffect(() => {
    if (skipInitialScreenParamSyncRef.current) {
      skipInitialScreenParamSyncRef.current = false;
      return;
    }

    addQueryParam("show", currentComp);
  }, [currentComp]);

  useEffect(() => {
    setActiveViewPlaceId(viewPlaceId);
  }, [viewPlaceId]);

  // The place-detail modal is driven by React state (activeViewPlaceId), which
  // the app updates via add/removeQueryParam — but the browser Back button fires
  // a raw `popstate` that only changes the URL. Sync the modal to the real URL on
  // Back so the hardware/browser Back button closes the detail as expected.
  useEffect(() => {
    const syncFromUrl = () => {
      if (typeof window === "undefined") return;
      const vp = new URLSearchParams(window.location.search).get("view-place");
      setActiveViewPlaceId(vp || null);
    };
    window.addEventListener("popstate", syncFromUrl);
    return () => window.removeEventListener("popstate", syncFromUrl);
  }, []);

  useEffect(() => {
    fetchCurrentUserLocation();
  }, []);

  useEffect(() => {
    if (user?.userId) {
      fetchUserFollowing();
      fetchAllUsersForTagging();
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (e) => {};
    if (selectedPostInModal) setIsOpen(true);
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedPostInModal]);

  return (
    <>
      <div className="w-full h-[100dvh] max-h-[100dvh] overflow-hidden pb-14">
        {renderScreen(currentComp)}
        {activeViewPlaceId && (
          <PlaceDetailsModalForMobile
            placeId={activeViewPlaceId}
            handleReset={() => removeQueryParam("view-place")}
            handleMapRoutesArrive={() => {}}
            handleMapRoutesDepart={() => {}}
            handlePostCardClick={handlePostCardClick}
            userFollowing={userFollowing}
            setUserFollowing={setUserFollowing}
            userLocation={currentUserLocation}
            openPlaceAddModal={openPlaceAddModal}
          />
        )}
      </div>
      {isAddPlaceModalOpen && (
        <PlaceAddModal
          isOpen={isAddPlaceModalOpen}
          onClose={closePlaceAddModal}
        />
      )}
      {isOpen && (
        <Modal
          handleClose={closeContentModalPopup}
          isClosing={isClosing}
          position={position}
          isOpen={isOpen || !!selectedPostInModal}
          isPiP={isPiP}
        >
          <ContentViewModal
            ref={popupRef}
            allPosts={[]}
            closePopup={closeContentModalPopup}
            setAllPosts={() => {}}
            currentUserFollowing={userFollowing}
            setCurrentUserFollowing={setUserFollowing}
            allUsersForTagging={allUsersForTagging}
            selectedPostInModal={selectedPostInModal}
            setSelectedPostInModal={setSelectedPostInModal}
            completeRefresh={() => {}}
            isAnimating={isAnimating}
            popupStyle={popupStyle}
            isPiP={isPiP}
            setIsPiP={setIsPiP}
            playerRef={playerRef}
            handleDisablePIP={handleDisablePIP}
            handleEnablePIP={handleEnablePIP}
          />
        </Modal>
      )}
    </>
  );
};

export default PlacesMobileScreen;
